import ArrowRight2Icon from '../../../../../../assets/svgs/ArrowRight2'
import BookmarkIcon from '../../../../../../assets/svgs/Bookmark'
import InfoLight from '../../../../../../assets/svgs/InfoLight'
import LiveIcon from '../../../../../../assets/svgs/Live'
import NotesIcon from '../../../../../../assets/svgs/Notes'
import PenIcon from '../../../../../../assets/svgs/Pen'
import RingIcon from '../../../../../../assets/svgs/Ring'
import TableIcon from '../../../../../../assets/svgs/Table'

/*
 * The people illustration is loaded as a file, not as the People2 React
 * component it used to be. That component is 323 KB of markup wrapping a
 * base64-encoded bitmap, and importing it compiled the whole thing into the main
 * JS bundle — +237 KB gzipped, a 35% increase, for one decorative drawing that
 * is parsed by the JS engine on every page load. As an <img> src webpack emits
 * it as a separate cached asset instead. It renders identically.
 */
import peopleIllustration from '../../../../../../assets/images/people-illustration.svg'
import useWindowWidth from '../../../../../../hooks/useWindowWidth'

import ClaimedBooth from './ClaimedBooth'

import {
  compactNumber,
  formatShortDate,
  joinParts,
  monogramColors,
  truncate,
} from '../../../format'

import style from './style.module.css'

const SCORE_HINT =
  'Activity score out of 10 — how much this company has been posting recently across jobs, news, events and courses.'

/**
 * The right-hand "Activity" rows, in priority order. Only the first three with a
 * count above zero are rendered, so a company with no news falls through to
 * courses rather than showing a zero.
 */
const ACTIVITY_ROWS = [
  { key: 'openJobs', singular: 'open role', plural: 'open roles', Icon: PenIcon },
  { key: 'news', singular: 'news item', plural: 'news items', Icon: NotesIcon },
  { key: 'events', singular: 'event', plural: 'events', Icon: RingIcon },
  { key: 'courses', singular: 'course', plural: 'courses', Icon: NotesIcon },
]

/**
 * One company card — the original three-column Figma layout, driven by real data.
 *
 * The geometry is the design as it was first built: two 85x185 white side panels
 * flanking a 140px middle column, 8px type, the people/table illustration, the
 * gold score. What changed is only what fills it. Every string here comes from
 * the API:
 *
 *   left "Signals"   <- company.signals   (was "New product / Currently hiring")
 *   left "Focus"     <- company.tags      (was the "Kristin Watson" blurb)
 *   middle logo      <- monogram + name   (was a hardcoded <TeslaLogo/>)
 *   middle score     <- company.score     (was a hardcoded 9.9/10)
 *   middle overlay   <- open roles        (was "4 Tesla employees online")
 *   right live       <- company.isLive    (was an always-on "live launch")
 *   right "Activity" <- company.counts    (was hackathon / survey / live Q&A)
 *
 * The four grey avatar circles are the one part of the original composition not
 * restored: they asserted named employees were online, and there is no presence
 * data behind them. The badge they sat in now carries the open-role count, which
 * is the number someone scanning the grid actually wants.
 */
const CompanyCard = ({ company, onOpen, onToggleSave }) => {
  const isMobile = useWindowWidth() <= 768

  // A company that has written its booth gets a stand of its own rather than
  // the observed-data tile. The three-column card below is right for a company
  // we only scraped; it has nothing to say about one that has a voice.
  if (company.hasNarrative || company.hasOpenSprint) {
    return <ClaimedBooth company={company} onOpen={onOpen} onToggleSave={onToggleSave} />
  }

  const counts = company.counts || {}
  const displayName = company.name || 'this company'

  const monogram = monogramColors(company.orgKey)
  const initials =
    company.initials || String(company.name || '?').trim().slice(0, 2).toUpperCase()

  // Number(null) is 0, and 0 is finite — so a null score rendered as "0.0/10".
  // A booth with no observed activity has no score at all.
  const hasScore = company.score !== null && company.score !== undefined
  const score = hasScore ? Number(company.score) : null

  const sprint = company.sprint || null

  /*
   * A booth with a live sprint leads with the sprint, and a booth whose content
   * is entirely authored (the showcase company) has no scraped signals or tags
   * at all — the panels would otherwise render as empty white boxes. So the
   * observed content is the default and the authored content is the fallback,
   * per panel, rather than the card assuming everything came from the pipeline.
   */
  const sprintLines = sprint
    ? [
        sprint.roles.length
          ? `${sprint.roles.length} ${sprint.roles.length === 1 ? 'role' : 'roles'} open`
          : null,
        sprint.spotsLeft !== null ? `${sprint.spotsLeft} places left` : null,
        formatShortDate(sprint.startsAt),
      ].filter(Boolean)
    : []

  const observedSignals = (company.signals || []).filter((s) => s && s.label).slice(0, 3)
  const signals = sprint
    ? sprintLines.map((label, i) => ({ key: `sprint-${i}`, label }))
    : observedSignals

  const signalsTitle = sprint ? 'Sprint' : 'Signals'

  const observedTags = (company.tags || []).slice(0, 3)
  const tags = observedTags

  const openRoles = Number(counts.openJobs)
  const hasOpenRoles = Number.isFinite(openRoles) && openRoles > 0

  const activity = ACTIVITY_ROWS.map((row) => {
    const value = Number(counts[row.key])
    return Number.isFinite(value) && value > 0 ? { ...row, value } : null
  })
    .filter(Boolean)
    .slice(0, 3)

  // The mobile bubble stands in for the old employee blurb: whatever real
  // context we actually hold, or nothing at all.
  const bubbleText = joinParts(
    [company.location, signals.length ? signals[0].label : null],
    ' · '
  )

  const handleSave = (event) => {
    // The bookmark sits over the card's click surface; without this the modal
    // would open on every save tap.
    event.stopPropagation()
    onToggleSave(company)
  }

  return (
    <div className={style.companyCardShell}>
      {/*
        Fair-floor signalling. A booth running a live hiring sprint is the
        reason to walk up to it, so it cannot look like every other booth — this
        is the one thing on the card that is not a count.
      */}
      {company.hasOpenSprint && (
        <span className={style.companyCard_flag}>
          <LiveIcon />
          48h sprint
        </span>
      )}

      {!company.hasOpenSprint && company.hasNarrative && (
        <span className={`${style.companyCard_flag} ${style.companyCard_flag_claimed}`}>
          Company profile
        </span>
      )}

      <button
        type="button"
        className={style.companyCard}
        onClick={() => onOpen(company)}
        data-log-action="spotlight_open_company"
      >
        <div className={style.companyCard_left}>
          {signals.length > 0 && (
            <div className={style.companyCard_events}>
              <div className={style.companyCard_left_title}>
                <span>{signalsTitle}</span>
              </div>

              <ul className={style.companyCard_events_list}>
                {signals.map((signal) => (
                  <li
                    key={signal.key || signal.label}
                    className={style.companyCard_events_list_item}
                  >
                    {truncate(signal.label, 28)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Scraped taxonomy tags where we have them; the company's own one-line
              pitch where we do not. Both answer "what is this place". */}
          {(tags.length > 0 || company.tagline) && (
            <div className={style.companyCard_people}>
              <div className={style.companyCard_left_title}>
                <span>{tags.length > 0 ? 'Focus' : 'In their words'}</span>
              </div>

              <div className={style.companyCard_people_desc}>
                <span>
                  {tags.length > 0 ? tags.join(' · ') : truncate(company.tagline, 110)}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className={style.companyCard_middle}>
          <div className={style.companyCard_logoBlock}>
            <div className={style.companyCard_logo}>
              <span
                className={style.companyCard_monogram}
                style={{
                  background: monogram.background,
                  color: monogram.ink,
                  boxShadow: `inset 0 0 0 1.5px ${monogram.foreground}`,
                }}
                aria-hidden="true"
              >
                {initials}
              </span>
              <span className={style.companyCard_logo_subname}>{company.name}</span>
            </div>

            {hasScore && (
              <div className={style.companyCard_rank} title={SCORE_HINT}>
                <span className={style.companyCard_rank_num}>
                  {score.toFixed(1)}/<span>10</span>
                </span>
                <InfoLight />
              </div>
            )}
          </div>

          {isMobile && bubbleText && (
            <div className={style.companyCard_employeeMessage}>
              <div>
                <span>{truncate(bubbleText, 80)}</span>
              </div>
            </div>
          )}

          <div className={style.companyCard_employees}>
            <div className={style.companyCard_employees_peopleImage} aria-hidden="true">
              <img src={peopleIllustration} alt="" loading="lazy" />
            </div>
            <div className={style.companyCard_employees_tableImage} aria-hidden="true">
              <TableIcon />
            </div>

            {/* The overlay carries whichever number is the reason to walk up:
                places left in a live sprint, otherwise the open-role count. */}
            {sprint ? (
              <div className={style.companyCard_employeesBlock}>
                <div className={style.companyCard_employeesBlock_list}>
                  <div className={style.companyCard_employeesBlock_count}>
                    <span>{sprint.spotsLeft !== null ? sprint.spotsLeft : sprint.roles.length}</span>
                  </div>
                </div>
                <div className={style.companyCard_employeesBlock_text}>
                  {sprint.spotsLeft !== null ? 'places left. ' : 'roles. '}
                  <span>Register</span>
                </div>
              </div>
            ) : (
              hasOpenRoles && (
                <div className={style.companyCard_employeesBlock}>
                  <div className={style.companyCard_employeesBlock_list}>
                    <div className={style.companyCard_employeesBlock_count}>
                      <span>{compactNumber(openRoles)}</span>
                    </div>
                  </div>
                  <div className={style.companyCard_employeesBlock_text}>
                    open roles. <span>View</span>
                  </div>
                </div>
              )
            )}
          </div>
        </div>

        <div className={style.companyCard_right}>
          <div className={style.companyCard_right_content}>
            {(sprint || company.isLive) && (
              <div className={style.companyCard_live}>
                <LiveIcon />
                <span>
                  {sprint
                    ? 'registration open'
                    : truncate(company.liveLabel || 'live now', 22)}
                </span>
              </div>
            )}

            {/* A live sprint replaces the activity breakdown: the roles it is
                actually hiring for beat a count of news items. */}
            {sprint && sprint.roles.length > 0 ? (
              <div className={style.companyCard_tryNow}>
                <div className={style.companyCard_tryNow_title}>
                  <span>Hiring for</span>
                </div>

                <ul className={style.companyCard_roleList}>
                  {sprint.roles.slice(0, 3).map((role) => (
                    <li key={role}>{truncate(role, 26)}</li>
                  ))}
                </ul>
              </div>
            ) : (
              activity.length > 0 && (
                <div className={style.companyCard_tryNow}>
                  <div className={style.companyCard_tryNow_title}>
                    <span>Activity</span>
                  </div>

                  <div className={style.companyCard_tryNow_actions}>
                    {activity.map(({ key, value, singular, plural, Icon }) => (
                      <div key={key} className={style.companyCard_tryNow_actions_item}>
                        <Icon />

                        <div className={style.companyCard_tryNow_actions_item_text}>
                          <span>{compactNumber(value)}</span>
                          <span>
                            {value === 1 ? singular : plural} <ArrowRight2Icon />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>

          {/*
            Not a button: the whole card already opens the modal, so a second
            control would be a duplicate tab stop announcing the same action.
          */}
          <span className={style.companyCard_right_seeMore}>See more details</span>
        </div>
      </button>

      {/*
        A sibling of the card button rather than a child: a <button> inside a
        <button> is invalid HTML and assistive tech drops the inner control.
      */}
      <button
        type="button"
        className={
          company.saved
            ? `${style.companyCard_save} ${style.companyCard_save_active}`
            : style.companyCard_save
        }
        onClick={handleSave}
        aria-label={company.saved ? `Remove ${displayName} from saved` : `Save ${displayName}`}
        aria-pressed={Boolean(company.saved)}
        data-log-action="spotlight_toggle_save"
      >
        <BookmarkIcon />
      </button>
    </div>
  )
}

export default CompanyCard
